import { createMemo, onCleanup, onMount, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Outlet, useLocation, useNavigate, useRouterState } from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { HabitRow } from "@habits/shared";

import { appState, Skeleton } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";

import { useHabitsQuery } from "@habits/shared/solid";

import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useTheme } from "@/providers/theme-provider";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";

// Layout module parts
import { useInteractionFlag } from "@fleet/ui";
import { AppLayoutContext } from "../context";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { AppLayoutDialogs } from "./app-layout-dialogs";
import { useAppDialogs } from "../hooks/use-app-dialogs";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";
import { usePageSwipeGestures } from "../hooks/use-page-swipe-gestures";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

/** Top-level tabs the horizontal page-swipe pages through, left → right. */
const SWIPE_TAB_PATHS = ["/", "/calendar", "/stats"];

/**
 * Derives a stable "route section" key from a pathname so the page-entrance
 * animation fires once per real navigation — and never on the in-route day /
 * month swipes (Today's day carousel, Calendar's month pager) or on deep-link
 * search params (`?day=`, `?habit=`), all of which leave the pathname intact.
 * Trailing slashes are normalised so `/profile` and `/profile/` share a key.
 */
function routeSectionKey(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/u, "");
  return trimmed === "" ? "/" : trimmed;
}

interface AppLayoutContentProps {
  db: AbstractPowerSyncDatabase | null;
}

/**
 * Granular skeleton for the main content region (<Outlet />) shown while the
 * local PowerSync DB is still initializing. Mirrors the typical screen layout
 * (header line + a list of rows) so the shell looks alive instead of blank.
 */
function ContentRegionSkeleton() {
  return (
    <div class="flex flex-1 flex-col gap-4 p-4 sm:p-6" aria-hidden="true">
      <Skeleton class="h-8 w-40" />
      <div class="flex flex-col gap-2">
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
      </div>
    </div>
  );
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  // Keyboard-vs-pointer activity tracking (and the `body.keyboard-navigation`
  // class) is owned by <InteractionProvider>; only the sidebar-specific Escape
  // handler remains here.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

export function AppLayoutContent(props: AppLayoutContentProps) {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const routerState = useRouterState();
  const isNavigating = () => routerState().status === "pending";

  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const theme = useTheme();

  // Single subscription to habits to derive next sort_order on creation and
  // to resolve `?edit=<id>` (URL-addressable) to its row in a permission-aware way.
  const habits = useHabitsQuery({
    db: () => props.db,
    drizzle,
    userId,
    includeArchived: () => true,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const habitsById = createMemo(() => {
    const map = new Map<string, HabitRow>();
    for (const row of habits.data ?? []) {
      map.set(row.id, row);
    }
    return map;
  });

  const {
    habitDialogOpen,
    setHabitDialogOpen,
    editingHabit,
    confirmDeleteOpen,
    setConfirmDeleteOpen,
    isSidebarOpen,
    setIsSidebarOpen,
    shortcutsOpen,
    setShortcutsOpen,
    openNewHabitDialog,
    openEditHabitDialog,
    // `habitsById` is consumed reactively INSIDE useAppDialogs (memos);
    // the rule cannot see it when passed as argument → false positive.
    // oxlint-disable-next-line solid/reactivity
  } = useAppDialogs(location, habitsById);

  useAppShortcuts({
    navigate,
    theme,
    setShortcutsOpen,
    setIsSidebarOpen,
    openNewHabitDialog,
  });

  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). The page swipe listens on the window and would otherwise fire
  // underneath it, so ownership is announced in the interaction context rather
  // than re-derived from the DOM by each gesture.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  // Horizontal swipe between the top-level tabs (Today / Calendar / Stats).
  const pageSwipe = usePageSwipeGestures({
    location,
    navigate,
    orderedPaths: () => SWIPE_TAB_PATHS,
  });

  const nextSortOrder = createMemo(() => {
    const rows = habits.data ?? [];
    return rows.reduce((max, row) => Math.max(max, (row.sort_order ?? 0) + 1), 0);
  });

  return (
    <AppBackground
      class="flex h-dvh text-foreground overflow-hidden"
      isBlur={theme.isBlur()}
      background={theme.tokens()?.background}
      patternType={theme.tokens()?.patternType}
      patternOpacity={theme.tokens()?.patternOpacity}
      glow0={theme.tokens()?.glow0 ?? "var(--glow-0)"}
      glow1={theme.tokens()?.glow1 ?? "var(--glow-1)"}
      glow2={theme.tokens()?.glow2 ?? "var(--glow-2)"}
    >
      {/* Skip-to-content: first focusable element; visually hidden until focused
          so keyboard users can jump past the sidebar/header into the content. */}
      <a
        href="#main-content"
        class="sr-only z-[200] rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground shadow-lg focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
      >
        {t(msg`Skip to content`)}
      </a>

      <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

      {/* ── Main content ───────────────────────────────────────── */}
      <main
        id="main-content"
        tabindex="-1"
        class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]"
        {...pageSwipe.handlers}
      >
        <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
        <OfflineBanner />
        <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
          <AppLayoutContext.Provider
            value={{
              openNewHabitDialog,
              openEditHabitDialog,
            }}
          >
            <div
              class="flex flex-1 flex-col min-h-0 h-full will-change-transform"
              style={{
                transform:
                  pageSwipe.isSwiping() || pageSwipe.swipeAnimating()
                    ? `translate3d(${pageSwipe.swipeTx()}px, 0, 0)`
                    : "none",
                transition: pageSwipe.swipeAnimating()
                  ? "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)"
                  : "none",
              }}
            >
              {/* Static shell renders instantly; only the data-bound content
                  region shows a granular skeleton until the local DB is ready. */}
              <Show when={props.db} fallback={<ContentRegionSkeleton />}>
                {/* Page-entrance: keyed by route section so every real
                    navigation slides up + fades in once. In-route day/month
                    swipes and `?day=`/`?habit=` deep-links keep the same
                    pathname → same key → no re-animation. The keyed wrapper is
                    a content-flow div (no layout shift); GPU-only props. */}
                <Show keyed when={routeSectionKey(location().pathname)}>
                  <div class="flex flex-1 flex-col min-h-0 animate-slide-in-up will-change-[transform,opacity]">
                    <Outlet />
                  </div>
                </Show>
              </Show>
            </div>
          </AppLayoutContext.Provider>
        </div>
      </main>

      <AppLayoutDialogs
        habitDialogOpen={habitDialogOpen}
        setHabitDialogOpen={setHabitDialogOpen}
        editingHabit={editingHabit}
        nextSortOrder={nextSortOrder}
        shortcutsOpen={shortcutsOpen}
        setShortcutsOpen={setShortcutsOpen}
        confirmDeleteOpen={confirmDeleteOpen}
        setConfirmDeleteOpen={setConfirmDeleteOpen}
      />
    </AppBackground>
  );
}
